import React from "react";
import { motion } from "framer-motion";
import {
  FaCloud,
  FaServer,
  FaShieldAlt,
  FaChartLine,
} from "react-icons/fa";

// Categories with services
const serviceCategories = [
  {
    category: "Design",
    services: [
      {
        icon: <FaCloud size={28} />,
        title: "Cloud Architecture",
        desc: "Design secure, scalable cloud architectures on AWS, Azure, GCP.",
      },
      {
        icon: <FaShieldAlt size={28} />,
        title: "Security Planning",
        desc: "IAM, firewall, compliance and best practice security designs.",
      },
    ],
  },
  {
    category: "Build",
    services: [
      {
        icon: <FaCloud size={28} />,
        title: "Implementation",
        desc: "Deploy cloud resources with automation and infrastructure as code.",
      },
    ],
  },
  {
    category: "Operation",
    services: [
      {
        icon: <FaServer size={28} />,
        title: "Monitoring & Management",
        desc: "24/7 monitoring, logging, and performance management.",
      },
    ],
  },
  {
    category: "Optimization",
    services: [
      {
        icon: <FaChartLine size={28} />,
        title: "Cost & Performance",
        desc: "Right-sizing, cost reduction, and tuning for efficiency.",
      },
    ],
  },
  {
    category: "Migration",
    services: [
      {
        icon: <FaServer size={28} />,
        title: "Cloud & Legacy Migration",
        desc: "Move workloads safely from legacy to modern cloud setups.",
      },
    ],
  },
  {
    category: "Upgradation",
    services: [
      {
        icon: <FaServer size={28} />,
        title: "System Upgrades",
        desc: "Update existing cloud resources to latest versions seamlessly.",
      },
    ],
  },
  {
    category: "Deletion",
    services: [
      {
        icon: <FaShieldAlt size={28} />,
        title: "Resource Decommission",
        desc: "Secure deletion and cleanup of outdated cloud resources.",
      },
    ],
  },
];

const Services = () => {
  return (
    <section
      id = "services"
      style={{
        position: "relative",
        padding: "40px 20px", // Reduced top padding
        background: "linear-gradient(180deg, #020617 0%, #0f172a 100%)",
        color: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Glow effects */}
      <div style={glow("#38bdf8", "5%", "10%")} />
      <div style={glow("#6366f1", "75%", "50%")} />

      <div style={{ maxWidth: "1200px", margin: "0 auto", position: "relative" }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center", marginBottom: "40px" }} // Reduced header margin
        >
          <h2 style={{ fontSize: "2.3rem", fontWeight: "800", marginBottom: "10px" }}>
            Our <span style={gradientText}>Services</span>
          </h2>
          <p
            style={{
              color: "#c7d2fe",
              fontSize: "1rem",
              maxWidth: "650px",
              margin: "0 auto",
              lineHeight: "1.5",
            }}
          >
            Comprehensive cloud services across every stage: design, build, operate, optimize, migrate, upgrade, and delete.
          </p>
        </motion.div>

        {/* Categories Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "26px",
          }}
        >
          {serviceCategories.map((cat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
              whileHover={{ scale: 1.12 }} // Bigger hover
            >
              <div style={cardStyle}>
                <h3 style={{ fontSize: "1.45rem", marginBottom: "16px", color: "#38bdf8" }}>
                  {cat.category}
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  {cat.services.map((s, i) => (
                    <div
                      key={i}
                      style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}
                    >
                      <div style={iconWrap}>{s.icon}</div>
                      <div>
                        <h4 style={{ margin: 0, fontSize: "1.05rem" }}>{s.title}</h4>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "0.92rem",
                            color: "#94a3b8",
                            lineHeight: "1.4",
                          }}
                        >
                          {s.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Styles ---------- */

const cardStyle = {
  background: "rgba(15, 23, 42, 0.78)",
  backdropFilter: "blur(14px)",
  borderRadius: "18px",
  padding: "26px",
  border: "1px solid rgba(148, 163, 184, 0.15)",
  boxShadow: "0 25px 55px rgba(0,0,0,0.38)", // Slightly stronger shadow
  cursor: "pointer",
  transition: "all 0.1s ease", // Immediate hover effect
};

const iconWrap = {
  width: "44px",
  height: "44px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "12px",
  background: "linear-gradient(135deg,#38bdf8,#6366f1)",
  flexShrink: 0,
};

const glow = (color, left, top) => ({
  position: "absolute",
  width: "380px",
  height: "380px",
  background: color,
  filter: "blur(150px)",
  opacity: 0.2,
  left,
  top,
});

const gradientText = {
  background: "linear-gradient(90deg,#38bdf8,#6366f1)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

export default Services;
